import React, { useState, useEffect } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { Paper } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { fetchEmployeesLeave } from '../../../../services/LeaveService'
import LeaveEmployeeRegisterModal from './LeaveEmployeeRegisterModal'
import LeaveEmployeeUpdateModal from './LeaveEmployeeUpdateModal'

const Status = {
    APPLICABLE: 1,
    NOT_APPLICABLE: 0
}

const LeaveEmployeeListLayout = () => {
    const navigate = useNavigate()
    const DEFAULT_PAGE_SIZE = 10
    const [dtr, setDTR] = useState([])
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [selectionModel, setSelectionModel] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [totalCount, setTotalCount] = useState(0)
    const [refresh, setRefresh] = useState(false)
    const [responseMessage, setResponseMessage] = useState('')
    const [registerDialogOpen, setRegisterDialogOpen] = useState(false)
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false)
    const [selectedLeave, setSelectedLeave] = useState(null)

    const columns = [
        { field: 'employeeId', headerName: 'Employee ID', flex: 1 },
        { field: 'fullName', headerName: 'Full Name', flex: 3 },
        { field: 'vacationLeave', headerName: 'Vacation Leave', flex: 1 },
        { field: 'sickLeave', headerName: 'Sick Leave', flex: 1 },
        { field: 'emergencyLeave', headerName: 'Emergency Leave', flex: 1 },
        {
            field: 'maternityLeave',
            headerName: 'Maternity Leave',
            flex: 1,
            renderCell: (params) => {
                const status = params.value
                const badgeClass = status === Status.APPLICABLE ? 'badge-success' : 'badge-danger'
                const text = status === Status.APPLICABLE ? 'APPLICABLE' : 'NOT APPLICABLE'
                return <span className={badgeClass}>{text}</span>
            }
        },
        {
            field: 'paternityLeave',
            headerName: 'Paternity Leave',
            flex: 1,
            renderCell: (params) => {
                const status = params.value
                const badgeClass = status === Status.APPLICABLE ? 'badge-success' : 'badge-danger'
                const text = status === Status.APPLICABLE ? 'APPLICABLE' : 'NOT APPLICABLE'
                return <span className={badgeClass}>{text}</span>
            }
        }
    ]

    useEffect(() => {
        fetchEmployeeList(searchTerm, page, pageSize)
    }, [page, pageSize, refresh])

    const fetchEmployeeList = async (search, page, limit) => {
        setLoading(true)
        try {
            const filter = { page, limit, search }
            const response = await fetchEmployeesLeave(filter)
            const dailyRecords = response.data || []
            setTotalCount(response.count || 0)

            const formattedData = dailyRecords.map((record, index) => ({
                id: record.id || index,
                vacationLeave: record.vacationLeave,
                sickLeave: record.sickLeave,
                emergencyLeave: record.emergencyLeave,
                maternityLeave: record.maternityLeave,
                paternityLeave: record.paternityLeave,
                employeeId: record.employee.employeeId,
                fullName: `${record.employee.firstName} ${record.employee.lastName}`
            }))

            setDTR(formattedData)
        } catch (error) {
            console.error('Error fetching leave data:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value)
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            setPage(1)
            fetchEmployeeList(searchTerm, 1, pageSize)
        }
    }

    const handleOpenRegisterDialog = () => {
        setRegisterDialogOpen(true)
    }

    const handleCloseRegisterDialog = () => {
        setRegisterDialogOpen(false)
    }

    const handleCloseUpdateDialog = () => {
        setUpdateDialogOpen(false)
    }

    const handleRowClick = (params) => {
        setSelectedLeave(params.row)
        setUpdateDialogOpen(true)
    }

    const handleLeaveSubmission = async () => {
        setSubmitting(true)
        try {
            setRefresh((prev) => !prev)
        } catch (error) {
            console.error('Error submitting leave:', error)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="page-container">
            {loading && <p className="text-sm text-gray-500 mb-2">Loading...</p>}
            {responseMessage && <p className="text-sm text-emerald-600 mb-2">{responseMessage}</p>}

            {/* Search and Register Button */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <input
                    type="text"
                    placeholder="Search..."
                    className="input-field sm:max-w-xs"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                />

                <button onClick={handleOpenRegisterDialog} className="btn-primary sm:ml-auto" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Register Leave'}
                </button>
            </div>

            <Paper sx={{ width: '100%', height: { xs: 'calc(100vh - 300px)', sm: 'calc(100vh - 260px)' }, minWidth: 0 }}>
                <DataGrid
                    rows={dtr}
                    columns={columns}
                    loading={loading}
                    page={page - 1}
                    pageSize={pageSize}
                    rowCount={totalCount}
                    paginationMode="server"
                    initialState={{
                        pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } }
                    }}
                    pageSizeOptions={[5, 10, 25]}
                    onPaginationModelChange={(newPaginationModel) => {
                        setPage(newPaginationModel.page + 1)
                        setPageSize(newPaginationModel.pageSize)
                    }}
                    onRowSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
                    selectionModel={selectionModel}
                    onRowClick={handleRowClick}
                    sx={{
                        border: 0,
                        '& .MuiDataGrid-columnHeaders': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-cell': {
                            cursor: 'default'
                        }
                    }}
                />
            </Paper>

            <LeaveEmployeeRegisterModal
                open={registerDialogOpen}
                onClose={handleCloseRegisterDialog}
                onRegisterSuccess={handleLeaveSubmission}
            />

            <LeaveEmployeeUpdateModal
                open={updateDialogOpen}
                onClose={handleCloseUpdateDialog}
                onUpdateSuccess={handleLeaveSubmission}
                data={selectedLeave}
            />
        </div>
    )
}

export default LeaveEmployeeListLayout
